import React, { useState, useEffect } from 'react';
import { interviewersAPI } from '../../utils/api';
import toastr from 'toastr';
import { Drawer } from '../shared/DrawerManager';
import './ApplicationView.css';

/**
 * ApplicationView Component (Read-Only for Interviewers)
 * 
 * Displays application details for a candidate being interviewed
 * Read-only view - interviewers cannot modify status or add notes
 */
function ApplicationView({ interviewId, isOpen, onClose }) {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'timeline' | 'notes'

  useEffect(() => {
    if (isOpen && interviewId) {
      loadApplication();
    }
  }, [isOpen, interviewId]);

  const loadApplication = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await interviewersAPI.getApplicationForInterview(interviewId);
      setApplication(response.data.application);
    } catch (err) {
      console.error('[ApplicationView] Failed to load application:', err);
      setError(err.response?.data?.error || 'Failed to load application details');
      toastr.error('Failed to load application details');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'new': 'status-new',
      'reviewing': 'status-reviewing',
      'shortlisted': 'status-shortlisted',
      'questionnaire_sent': 'status-pending',
      'questionnaire_completed': 'status-progress',
      'screening_call': 'status-pending',
      'screening_passed': 'status-progress',
      'interview': 'status-scheduled',
      'assessment_sent': 'status-pending',
      'assessment_completed': 'status-completed',
      'offer': 'status-success',
      'hired': 'status-success',
      'rejected': 'status-rejected',
      'withdrawn': 'status-rejected',
      'declined': 'status-rejected'
    };
    return statusMap[status] || 'status-pending';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Application Details"
      width="lg"
    >
      {loading && (
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <div className="cyber-spinner"></div>
          <p>Loading application details...</p>
        </div>
      )}

      {error && (
        <div style={{ padding: '1rem', background: '#FEE2E2', color: '#991B1B', borderRadius: '6px', margin: '1rem' }}>
          {error}
        </div>
      )}

      {application && !loading && (
        <div className="application-view">
          {/* Tabs */}
          <div className="application-tabs">
            <button
              className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              Overview
            </button>
            <button
              className={`tab-button ${activeTab === 'timeline' ? 'active' : ''}`}
              onClick={() => setActiveTab('timeline')}
            >
              Timeline
            </button>
            <button
              className={`tab-button ${activeTab === 'notes' ? 'active' : ''}`}
              onClick={() => setActiveTab('notes')}
            >
              Notes ({application.internalComments?.length || 0})
            </button>
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="tab-content">
              {/* Status */}
              <div className="detail-section">
                <h3>Application Status</h3>
                <div className="detail-row">
                  <span className="label">Status:</span>
                  <span className={`status-badge ${getStatusBadgeClass(application.status)}`}>
                    {application.status?.replace(/_/g, ' ').toUpperCase() || 'N/A'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Applied:</span>
                  <span className="value">{formatDate(application.appliedAt)}</span>
                </div>
              </div>

              {/* Candidate Information */}
              <div className="detail-section">
                <h3>Candidate Information</h3>
                <div className="detail-row">
                  <span className="label">Name:</span>
                  <span className="value">
                    {application.candidateInfo?.firstName} {application.candidateInfo?.lastName}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="label">Email:</span>
                  <span className="value">{application.candidateInfo?.email}</span>
                </div>
                {application.candidateInfo?.phone && (
                  <div className="detail-row">
                    <span className="label">Phone:</span>
                    <span className="value">{application.candidateInfo.phone}</span>
                  </div>
                )}
                {application.candidateInfo?.location && (
                  <div className="detail-row">
                    <span className="label">Location:</span>
                    <span className="value">{application.candidateInfo.location}</span>
                  </div>
                )}
                {application.resume && (
                  <div className="detail-row">
                    <span className="label">Resume:</span>
                    <span className="value">
                      {application.resume?.fileUrl ? (
                        <a href={application.resume.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#1E40AF', textDecoration: 'none', fontWeight: '500' }}>
                          {application.resume.filename || 'Download Resume'}
                        </a>
                      ) : (
                        application.resume.filename || (typeof application.resume === 'string' ? application.resume : 'Resume attached')
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Ratings */}
              {(application.rating || application.ratings) && (
                <div className="detail-section">
                  <h3>Ratings</h3>
                  {application.ratings?.overall && (
                    <div className="detail-row">
                      <span className="label">Overall:</span>
                      <span className="value">{application.ratings.overall} / 5</span>
                    </div>
                  )}
                  {application.rating && !application.ratings?.overall && (
                    <div className="detail-row">
                      <span className="label">Rating:</span>
                      <span className="value">{application.rating} / 5</span>
                    </div>
                  )}
                </div>
              )}

              {/* Tags */}
              {application.tags && application.tags.length > 0 && (
                <div className="detail-section">
                  <h3>Tags</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                    {application.tags.map((tag, index) => (
                      <span key={index} className="tag-badge">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Assessment */}
              {application.assessmentInviteId && (
                <div className="detail-section">
                  <h3>Assessment</h3>
                  <div className="detail-row">
                    <span className="label">Status:</span>
                    <span className="value">
                      {application.assessmentCompletedAt ? 'Completed' : application.assessmentSentAt ? 'Sent' : 'N/A'}
                    </span>
                  </div>
                  {application.assessmentScore !== undefined && (
                    <div className="detail-row">
                      <span className="label">Score:</span>
                      <span className="value">{application.assessmentScore}%</span>
                    </div>
                  )}
                </div>
              )}

              {/* Cover Letter */}
              {application.coverLetter && (
                <div className="detail-section">
                  <h3>Cover Letter</h3>
                  <div className="cover-letter-content">
                    {application.coverLetter}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="tab-content">
              <div className="timeline-container">
                {application.timeline && application.timeline.length > 0 ? (
                  application.timeline
                    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
                    .map((entry, index) => (
                      <div key={index} className="timeline-entry">
                        <div className="timeline-dot"></div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <span className="timeline-action">{entry.action?.replace(/_/g, ' ')}</span>
                            <span className="timeline-date">{formatDate(entry.timestamp)}</span>
                          </div>
                          <div className="timeline-description">{entry.description}</div>
                          {entry.performedBy && (
                            <div className="timeline-performed-by">
                              By: {entry.performedBy.name || entry.performedBy.fullName || entry.performedBy.email}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                ) : (
                  <div className="empty-state">
                    <p>No timeline entries yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Notes Tab */}
          {activeTab === 'notes' && (
            <div className="tab-content">
              <div className="notes-container">
                {application.internalComments && application.internalComments.length > 0 ? (
                  application.internalComments
                    .sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt))
                    .map((comment, index) => (
                      <div key={index} className="comment-card">
                        <div className="comment-header">
                          <span className="comment-author">
                            {comment.addedBy?.name || comment.addedBy?.fullName || comment.addedBy?.email || 'Unknown'}
                          </span>
                          <span className="comment-date">{formatDate(comment.addedAt)}</span>
                        </div>
                        <div className="comment-content">{comment.comment}</div>
                        {comment.mentions && comment.mentions.length > 0 && (
                          <div className="comment-mentions">
                            Mentioned: {comment.mentions.map(m => m.name || m.fullName || m.email).join(', ')}
                          </div>
                        )}
                        {comment.pinned && (
                          <span className="comment-pinned">Pinned</span>
                        )}
                      </div>
                    ))
                ) : (
                  <div className="empty-state">
                    <p>No internal comments yet.</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

export default ApplicationView;


